<template>
  <ImageElement
    v-if="renderWithoutFigure"
    :id="id"
    ref="element"
    :src="src"
    :aspect-ratio="aspectRatio"
    :class="imageClass"
    :style="imageStyle"
    :alt="alt || ''"
    :height="height"
    :width="width"
    :lazy="lazy"
    :sizes="sizes"
    :do-not-convert="doNotConvert || isSvg"
    :preload="preload"
  />
  <PictureElement
    v-else
    ref="element"
    :src="src"
    :src-sm="srcSm"
    :src-md="srcMd"
    :src-lg="srcLg"
    :src-xl="srcXl"
    :aspect-ratio="aspectRatio"
    :aspect-ratio-sm="aspectRatioSm"
    :aspect-ratio-md="aspectRatioMd"
    :aspect-ratio-lg="aspectRatioLg"
    :aspect-ratio-xl="aspectRatioXl"
    :image-style="imageStyle"
    :image-class="imageStyle"
    :alt="alt || ''"
    :height="height"
    :width="width"
    :lazy="lazy"
    :sizes="sizes"
    :do-not-convert="doNotConvert || isSvg"
    :preload="preload"
  >
    <slot />
  </PictureElement>
</template>

<script setup lang="ts">
import { computed } from 'vue';
/**
 * When we habe 1 source for all viewports, we can use img with option srcset for high resolutions.
 * When we have different images for a breakpoint, we should use the "picture" element.
 */
import { PictureElement, ImageElement } from '@i22-td-smarthome/component-library';

export type CdnImageProps = {
  id?: string | undefined;
  src?: string | undefined;
  srcSm?: string | undefined;
  srcMd?: string;
  srcLg?: string;
  srcXl?: string | undefined;
  height?: number | undefined;
  width?: number | undefined;
  renderWithoutFigure?: boolean;

  // Custom AspectRatio for each Breakpoint
  aspectRatio?: string;
  aspectRatioSm?: string;
  aspectRatioMd?: string;
  aspectRatioLg?: string;
  aspectRatioXl?: string;

  // From ImageElementProps
  imageClass?: string;
  imageStyle?: string;
  alt?: string;
  lazy?: boolean;
  format?: 'jpeg' | 'webp' | string;
  doNotConvert?: boolean;
  sizes?: string; // "sm:100vw md:50vw lg:400px" https://v0.image.nuxtjs.org/components/nuxt-img#sizes
  preload?: boolean;
};

const props = withDefaults(defineProps<CdnImageProps>(), {
  id: undefined,
  src: undefined,
  srcSm: undefined,
  srcMd: undefined,
  srcLg: undefined,
  srcXl: undefined,
  aspectRatio: undefined,
  aspectRatioSm: undefined,
  aspectRatioMd: undefined,
  aspectRatioLg: undefined,
  aspectRatioXl: undefined,
  width: undefined,
  height: undefined,
  imageStyle: undefined,
  imageClass: undefined,
  alt: '',
  format: 'webp',
  sizes: '',
});

// We do not want to convert a svg.
const isSvg = computed(() => props.src?.endsWith('.svg'));
</script>
